import React, { useEffect, useState } from "react"

import useDeviceDetection from "../../hooks/useDeviceDetection"
import useIsMounted from "../../hooks/useIsMounted"
import useWindowSize from "../../hooks/useWindowSize"

import FooterSection from "./components/FooterSection"
import HeaderSection from "./components/HeaderSection"
import SectionRenderer from "./SectionRenderer"
import { isReviewsBuilderPage } from "./utils/pageUtils"
import { MinimalWebsiteBuilderV2Provider } from "./WebsiteBuilderV2Context"

const WebsiteBuilderV2PublicPage = ({
  practice,
  page,
  sections,
  theme,
  viewingInBuilder = false,
  deviceType: serverDeviceType = "desktop"
}) => {
  // CRITICAL: All hooks must be called before any conditional returns
  // This prevents React error #185 (Rendered more/fewer hooks than previous render)

  // Track if component is mounted to avoid hydration mismatches
  const isMounted = useIsMounted()

  // Use both device detection methods for maximum reliability
  // matchMedia-based detection (industry standard, matches CSS breakpoints exactly)
  const {
    isMobile: isMobileDeviceDetection,
    width: deviceDetectionWidth,
    height: deviceDetectionHeight
  } = useDeviceDetection(768)
  // Window size-based detection (responds to window resizing)
  const { isMobile: isMobileWindowSize, width: windowSizeWidth, height: windowSizeHeight } = useWindowSize(768)

  // Use server device type for initial render to prevent hydration mismatches
  // After mount, we can use client-side detection, but we'll keep it stable to avoid re-renders
  const [deviceType, setDeviceType] = useState(serverDeviceType)

  // Validate required props to prevent crashes (AFTER hooks)
  if (!practice) {
    console.error("WebsiteBuilderV2PublicPage: practice prop is required")
    return (
      <div className="website-builder-v2-public py-20 text-center">
        <h2 className="mb-4 text-2xl font-bold text-gray-800">Error loading page</h2>
        <p className="text-gray-600">Please try refreshing the page.</p>
      </div>
    )
  }

  // Ensure theme has safe defaults to prevent rendering errors
  const safeTheme = theme && typeof theme === "object" ? theme : {}

  // Ensure sections is always an array
  const safeSections = Array.isArray(sections) ? sections : []

  // Update device type based on client-side detection (handles orientation changes, resize, etc.)
  useEffect(() => {
    if (!isMounted) return

    // Determine device type with preference for maintaining mobile on actual mobile devices
    const determineClientDeviceType = () => {
      // Use windowSize dimensions if available, otherwise deviceDetection dimensions
      const width = windowSizeWidth !== null && windowSizeWidth !== undefined ? windowSizeWidth : deviceDetectionWidth
      const height =
        windowSizeHeight !== null && windowSizeHeight !== undefined ? windowSizeHeight : deviceDetectionHeight

      if (width === null || width === undefined) {
        return serverDeviceType
      }

      // If server detected as mobile device (based on user agent), keep it mobile
      // This handles landscape iPhones which have width > 768px but should still show mobile UI
      // Only switch to desktop if the viewport is genuinely large (both width and height)
      if (serverDeviceType === "mobile") {
        // For actual mobile devices, only show desktop if we have a large viewport
        // (e.g., iPad in landscape or desktop browser resized narrow then wide)
        const isLargeViewport = width >= 1024 && height !== null && height >= 600
        if (!isLargeViewport) {
          return "mobile"
        }
      }

      // If both detection methods agree, use that
      if (isMobileDeviceDetection === isMobileWindowSize) {
        return isMobileDeviceDetection ? "mobile" : "desktop"
      }

      // If they disagree and windowSize says mobile, prefer mobile
      if (isMobileWindowSize) {
        return "mobile"
      }

      // If they disagree and windowSize says desktop, but deviceDetection says mobile, prefer mobile
      if (isMobileDeviceDetection) {
        return "mobile"
      }

      // Fallback
      return "desktop"
    }

    const clientDeviceType = determineClientDeviceType()
    // Update device type whenever the client-side detection changes
    // This properly handles orientation changes (portrait/landscape)
    setDeviceType(clientDeviceType)
  }, [
    isMounted,
    isMobileDeviceDetection,
    isMobileWindowSize,
    deviceDetectionWidth,
    deviceDetectionHeight,
    windowSizeWidth,
    windowSizeHeight,
    serverDeviceType
  ])

  // Handle anchor scrolling when page loads
  useEffect(() => {
    // Only run on client side
    if (typeof window === "undefined") return

    const handleAnchorScroll = () => {
      try {
        const hash = window.location?.hash
        if (hash) {
          // Remove the # from the hash
          const elementId = hash.substring(1)
          const element = document.getElementById(elementId)

          if (element) {
            // Add a small delay to ensure the page is fully rendered
            setTimeout(() => {
              try {
                element.scrollIntoView({
                  behavior: "smooth",
                  block: "start"
                })
              } catch (e) {
                console.warn("Error scrolling to anchor:", e)
              }
            }, 500) // 500ms delay
          }
        }
      } catch (e) {
        console.warn("Error handling anchor scroll:", e)
      }
    }

    // Run on initial load
    handleAnchorScroll()

    // Also listen for hash changes (in case user navigates with anchors)
    window.addEventListener("hashchange", handleAnchorScroll)

    return () => {
      window.removeEventListener("hashchange", handleAnchorScroll)
    }
  }, [])

  // Handle missing or empty sections
  if (!safeSections || safeSections.length === 0) {
    return (
      <div className="website-builder-v2-public py-20 text-center">
        <h2 className="mb-4 text-2xl font-bold text-gray-800">Welcome to {practice?.name || "this practice"}</h2>
        <p className="text-gray-600">This page is being set up. Please check back soon!</p>
        <div className="mt-4 text-sm text-gray-500">
          Debug: No sections found for page &quot;{page?.title || "Unknown"}&quot;
        </div>
      </div>
    )
  }

  // First *visible* section by `order` (not array order) must drive header visibility so it matches render order.
  // hero/sub_hero embed their own header for full_image and shape variants, so hide the page header to avoid duplicates.
  const visibleSections = safeSections
    .filter((section) => section && typeof section === "object" && !section.hidden)
    .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))
  const firstSection = visibleSections[0]
  const isReviewsPage = isReviewsBuilderPage(page, practice)
  const firstSectionLayout = firstSection?.settings?.layout
  const isFullImageHeroLayout =
    firstSectionLayout === "full_image" ||
    (typeof firstSectionLayout === "string" && firstSectionLayout.startsWith("full_image_"))
  const shouldHideHeader =
    !isReviewsPage && // Always show header on reviews page
    firstSection &&
    (firstSection?.layout === "hero" || firstSection?.layout === "sub_hero") &&
    isFullImageHeroLayout

  return (
    <MinimalWebsiteBuilderV2Provider practice={practice} theme={safeTheme}>
      <div className="website-builder-v2-public">
        {/* Header - Always show on reviews page, hide if first section is HeroSection or SubHeroSection with full_image layout */}
        {!shouldHideHeader && (
          <HeaderSection
            section={{ id: "header", settings: {} }}
            practice={practice}
            theme={safeTheme}
            highlightedField={null}
            deviceType={deviceType}
          />
        )}

        <main id="main-content">
          {/* Render all visible sections using the SectionRenderer */}
          {visibleSections.map((section) => {
            if (!section || !section.id) {
              console.warn("WebsiteBuilderV2PublicPage: Invalid section found", section)
              return null
            }
            try {
              return (
                <SectionRenderer
                  key={section.id}
                  section={section}
                  practice={practice}
                  theme={safeTheme}
                  highlightedField={null}
                  viewingInBuilder={viewingInBuilder}
                  pageSlug={page?.slug}
                  defaultPageType={page?.defaultPageType}
                  deviceType={deviceType}
                  isCustom={page?.isCustom}
                />
              )
            } catch (error) {
              console.error("WebsiteBuilderV2PublicPage: Error rendering section", section.id, error)
              return null
            }
          })}
        </main>

        {/* Footer */}
        <FooterSection
          section={{ id: "footer", settings: {} }}
          practice={practice}
          theme={safeTheme}
          highlightedField={null}
          deviceType={deviceType}
        />
      </div>
    </MinimalWebsiteBuilderV2Provider>
  )
}

export default WebsiteBuilderV2PublicPage
